import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "theme";
const THEME_COLOR = { light: "#fbfaf7", dark: "#121110" };

export const readStoredTheme = () => {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch (error) {
    return null;
  }
};

export const systemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const applyTheme = (theme) => {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[theme]);
};

/**
 * Light/dark theme state.
 *
 * The initial value mirrors the inline script in public/index.html, so the
 * first paint already has the right theme. Once the visitor picks a theme it
 * is stored and the operating system preference is no longer followed.
 */
const useTheme = () => {
  const [theme, setTheme] = useState(() => readStoredTheme() || systemTheme());

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!window.matchMedia || readStoredTheme()) return undefined;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event) => setTheme(event.matches ? "dark" : "light");
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch (error) {
        // Private browsing or blocked storage: the theme still applies.
      }
      return next;
    });
  }, []);

  return [theme, toggleTheme];
};

export default useTheme;
